/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient } from '@angular/common/http'
import { Injectable, OnDestroy } from '@angular/core'
import type { DeleteFormReadingsCommand, PatchFormReadingsCommand, SubmitFormReadingsCommand } from '@izho-capcore/app-source-readings/api'
import { combineLatest, Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { Frequency } from '../../modules/charts/models/series.model'
import { DomainGraph, DomainObject } from '../models'
import * as ChartsApiResponse from '../models/api/charts-api-response.model'
import * as ChartsApiShared from '../models/api/charts-api-shared.model'
import * as LegacyReadingFormsApiResponse from '../models/api/legacy-readings-forms-api-response.model'
import * as ReadingFormResponsesApiResponse from '../models/api/reading-form-responses-api-response.model'
import * as ReadingFormsApiResponse from '../models/api/readings-forms-api-response.model'
import * as ScopesApiResponse from '../models/api/scopes-api-response.model'
import * as WatchApiResponse from '../models/api/watch-api-response.model'
import * as WatchProceduresApiResponse from '../models/api/watch-procedures-api-response.model'
import { AppConfig, AppConfigService } from './app-config.service'
import { FeatureFlagsService } from './feature-flag.service'

const collectDomainNodeFromAPI = (appConfig: AppConfig, domainObjectsFromAPI: any[]): DomainGraph.T => {
  const domainGraph = DomainGraph.fromDomainObjects(domainObjectsFromAPI as DomainObject.T[], appConfig)
  return domainGraph.reduce((graph: DomainGraph.T, node)=> {
    if (DomainGraph.isDisplayable(node)){
      const attributes = DomainObject.attributesOf(DomainGraph.dataOfNode(node))
      if(DomainGraph.isDisplayableAsSingleProperty(node)) {
        if (attributes.calculation === undefined){
          console.log(`invalid single property displayable ${JSON.stringify(node.data)}`)
        } else {
          attributes.value = attributes.calculation
          graph.push(node)
        }
      } else if (DomainGraph.isDisplayableAsSupplierMeter(node)) {
        if ((attributes.calculation === undefined) || (attributes.measures === undefined)){
          console.log(`invalid supplier meter displayable ${JSON.stringify(node.data)}`)
        } else {
          attributes.total = attributes.calculation
          attributes.tariffs = attributes.measures
          graph.push(node)
        }
      } else {
        graph.push(node)
      }
    } else {
      graph.push(node)
    }
    return graph
  }, [])
}

type ListReportingProcessesResponse = {
  reportingProcessId: string,
  reportingProcessName: string
}[]
type GetReportingProcessResponse = {
  reportingProcessId: string,
  reportingProcessName: string,
  reportingProcessModelId: string,
  visualizationConfiguration: Record<string, unknown>,
  visualizationId: string
}
type ListLatestOfEachPeriodReportsResponse = {
  period: string,
  builtAt: string
}[]
type GetReportReponse = {
  reportingProcessId:string,
  period: string,
  builtAt: Date,
  data: Record<string, unknown>
}

@Injectable({ providedIn: 'root' })
export class ApiService implements OnDestroy {

  private domainId: string
  private baseUrl:string
  private useVersionlessReadingForms = false

  private subscriptions  = [
    this.appConfigService.state$.subscribe((appConfig)=>{
      this.domainId = appConfig.domain.name
    }),
    this.featureFlags.versionLessReadingForms$.subscribe(({ isEnabled })=>{
      this.useVersionlessReadingForms = isEnabled
    })
  ]

  constructor (
    private http: HttpClient,
    private appConfigService: AppConfigService,
    private featureFlags: FeatureFlagsService
  ) {
    this.baseUrl = `${window.location.origin}/api`
  }

  ngOnDestroy (): void {
    this.subscriptions.forEach(subs => subs.unsubscribe())
  }

  // SCOPES
  getListScopes (): Observable<ScopesApiResponse.T> {
    return this.http.get<any>(`${this.baseUrl}/domains/${this.domainId}/scopes`)
      .pipe(map(ScopesApiResponse.parse))
  }

  // READINGS
  getListReadingsForms (scopeId: string): Observable<ReadingFormsApiResponse.T> {
    if (this.useVersionlessReadingForms){
      return this.http.get(
        `${this.baseUrl}/domains/${this.domainId}/manual-reading-batch-form/captain-api`,
        { params: { scopeId, removeVersion: true } }
      ).pipe(map(ReadingFormsApiResponse.parse))
    } else {
      return this.http.get(
        `${this.baseUrl}/domains/${this.domainId}/manual-reading-batch-form/captain-api`,
        { params: { scopeId } }
      ).pipe(
        map(LegacyReadingFormsApiResponse.parse),
        map(LegacyReadingFormsApiResponse.toLatest)
      )
    }

  }
  getListReadings (scopeId: string, formId: string): Observable<ReadingFormResponsesApiResponse.T> {
    return this.http.get<any>(`${this.baseUrl}/domains/${this.domainId}/manual-reading-batch-form/${formId}`, {
      params: {
        scopeId,
        ...(this.useVersionlessReadingForms ? { removeVersion: true } : {})
      }
    }).pipe(map(ReadingFormResponsesApiResponse.parse))
  }
  postReading (scopeId: string, newValue: SubmitFormReadingsCommand): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/domains/${this.domainId}/manual-reading-batch-form`, newValue, {
      params: {
        scopeId,
        ...(this.useVersionlessReadingForms ? { removeVersion: true } : {})
      }
    })
  }
  updateReading (scopeId: string, updateValue: PatchFormReadingsCommand): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/domains/${this.domainId}/manual-reading-batch-form`, updateValue, {
      params: {
        scopeId,
        ...(this.useVersionlessReadingForms ? { removeVersion: true } : {})
      }
    })
  }
  deleteReading (scopeId: string, deleteValue: DeleteFormReadingsCommand): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/domains/${this.domainId}/manual-reading-batch-form`, deleteValue, {
      params: {
        scopeId,
        ...(this.useVersionlessReadingForms ? { removeVersion: true } : {})
      }
    })
  }

  // REPORTS
  listReportingProcesses (scopeId: string): Observable<ListReportingProcessesResponse> {
    return this.http.get<ListReportingProcessesResponse>(
      `${this.baseUrl}/domains/${this.domainId}/reporting-processes`, { params: { scopeId } }
    )
  }
  getReportingProcess (reportingProcessId: string): Observable<GetReportingProcessResponse> {
    return this.http.get<GetReportingProcessResponse>(
      `${this.baseUrl}/domains/${this.domainId}/reporting-processes/${reportingProcessId}`
    )
  }
  listLatestOfEachPeriodReports (reportingProcessId: string): Observable<ListLatestOfEachPeriodReportsResponse> {
    return this.http.get<ListLatestOfEachPeriodReportsResponse>(
      `${this.baseUrl}/domains/${this.domainId}/reporting-processes/${reportingProcessId}/reports`
    )
  }
  getReport (reportingProcessId: string, reportBuiltAt: Date): Observable<GetReportReponse> {
    return this.http.get<GetReportReponse>(
      `${this.baseUrl}/domains/${this.domainId}/reporting-processes/${reportingProcessId}/reports/${reportBuiltAt.toISOString()}`
    )
  }
  rebuildReport (reportingProcessId:string, period:string): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/domains/${this.domainId}/reporting-processes/${reportingProcessId}/reports`,
      { period }
    )
  }
  // Charts
  listChartsInScope (scopeId:string): Observable<ChartsApiResponse.T>{
    return this.http.get<any>(`${this.baseUrl}/domains/${this.domainId}/charts`, { params: { scopeId } }).pipe(
      map(ChartsApiResponse.parse)
    )
  }
  putChart ({ scopeId, id, name, axes, series, constants, plotBands, visibility, displayCumulativeAsRate }:ChartsApiShared.Chart):Observable<void>{
    return this.http.put<any>(`${this.baseUrl}/domains/${this.domainId}/charts/${id}`, {
      scopeId,
      name,
      axes,
      series,
      constants,
      plotBands,
      visibility,
      displayCumulativeAsRate
    })
  }
  deleteChart (chartId:string): Observable<void> {
    return this.http.delete<any>(`${this.baseUrl}/domains/${this.domainId}/charts/${chartId}`)
  }

  // PROPERTY VALUES
  getTimeseriesValues ({ scopeId, id, fromUtcDate, untilUtcDate }:{
    scopeId: string,
    id: string,
    fromUtcDate: Date,
    untilUtcDate: Date
  }): Observable<{ [strategy: string]: {t: string, val: number}[] }> {
    const interval = `${fromUtcDate.toISOString()}/${untilUtcDate.toISOString()}`
    return this.http.get<any>(`${this.baseUrl}/domains/${this.domainId}/timeseries/${id}/values`, { params: { scopeId, interval } })
      .pipe(
        map(response => {
          if (!('raw' in response)) throw Error('no mandatory strategy "raw" in api response')
          const mappedResponse: { [strategy: string]: {t: string, val: number}[] } = {}
          for (const strategy in response)
            mappedResponse[strategy] = response[strategy].map(({ t, val }) => ({ t, val }))
          return mappedResponse
        })
      )
  }
  getTimeseriesAggregatedValues ({ scopeId, id, fromUtcDate, untilUtcDate, frequency }:{
    scopeId: string,
    id: string,
    fromUtcDate: Date,
    untilUtcDate: Date,
    frequency: Frequency
  }): Observable<{ [strategy: string]: {t: string, val: number}[] }> {
    const interval = `${fromUtcDate.toISOString()}/${untilUtcDate.toISOString()}`
    return this.http.get<any>(`${this.baseUrl}/domains/${this.domainId}/timeseries/${id}/aggregated-values`, { params: { scopeId, interval, frequency } })
      .pipe(
        map(response => {
          if (!('raw' in response)) throw Error('no mandatory strategy "raw" in api response')
          const mappedResponse: { [strategy: string]: {t: string, val: number}[] } = {}
          for (const strategy in response)
            mappedResponse[strategy] = response[strategy].map(({ t, val }) => ({ t, val }))
          return mappedResponse
        })
      )
  }

  // DOMAIN OBJECTS
  getDomainGraph (scopeId: string): Observable<DomainGraph.T> {
    return combineLatest([
      this.appConfigService.state$,
      this.http.get<any>(`${this.baseUrl}/domains/${this.domainId}/domain-objects`, { params: { scopeId } })
    ])
      .pipe(map(([ config, domainObjectsFromAPI ]) => collectDomainNodeFromAPI(config, domainObjectsFromAPI)))
  }

  // Watches
  listWatchProcedures (scopeId: string): Observable<WatchProceduresApiResponse.T[]> {
    return this.http.get(`${this.baseUrl}/domains/${this.domainId}/watch-procedures?`, { params: { scopeId } })
      .pipe(map(WatchProceduresApiResponse.parse))
  }

  listWatchesOfProcedure (watchProcedureId: string): Observable<string[]> {
    return this.http.get(`${this.baseUrl}/domains/${this.domainId}/watch-procedures/${watchProcedureId}/watches`)
      .pipe(
        map(response => {
          if (!Array.isArray(response) || !response.every(v => typeof v === 'string')) throw Error('Invalid api response format')

          return response
        })
      )
  }
  getWatch (watchProcedureId: string, period: string): Observable<WatchApiResponse.T> {
    return this.http.get(`${this.baseUrl}/domains/${this.domainId}/watch-procedures/${watchProcedureId}/watches/${period}`)
      .pipe(map(WatchApiResponse.parse))
  }

  // Remote Apps
  listRemoteApps (scopeId: string): Observable<{ id: string, name: string }[]> {
    return this.http.get<{ id: string, name: string }[]>(
      `${this.baseUrl}/remote-apps`, { params: { scopeId } }
    )
  }

  connectToRemoteApp (appId: string, scopeId: string): Observable<{ url: string }> {
    return this.http.post<{ url: string }>(
      `${this.baseUrl}/remote-apps/${appId}/connect`, null, { params: { scopeId } }
    )
  }
}
